import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { useFeatures } from "@/lib/features";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import {
  ShoppingCart, BarChart3, TrendingUp, Zap, Shield, Globe,
  ArrowRight, Check, ChevronRight, Target, Eye, Bell, DollarSign,
  Users, Package, Activity, LineChart, Lock, Layers, Percent, AlertTriangle,
  Menu, X
} from "lucide-react";

const FALLBACK_PLANS = [
  { name: "Extra Small", limit: 100, prices: { monthly: 45, weekly: 50, daily: 55 } },
  { name: "Small", limit: 1000, prices: { monthly: 75, weekly: 105, daily: 125 }, popular: true },
  { name: "Medium", limit: 2500, prices: { monthly: 75, weekly: 95, daily: 130 } },
  { name: "Large", limit: 5000, prices: { monthly: 99, weekly: 159, daily: 275 } },
  { name: "Enterprise", limit: 25000, prices: { monthly: 245, weekly: 395, daily: 1499 } },
  { name: "Extreme", limit: 50000, prices: { monthly: 729, weekly: 1049, daily: 2799 } },
];

function Header() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = [
    { href: "#features", label: t("landing.nav_features") },
    { href: "#for-whom", label: t("landing.nav_for_whom") },
    { href: "/vergelijk", label: "Vergelijk", isLink: true },
    { href: "#pricing", label: t("nav.pricing") },
    { href: "#faq", label: "FAQ" },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-[64px] sm:h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="landing-logo"><img src="/logo.jpg" alt="Smartpriced" className="h-8 sm:h-10" /></Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) =>
            link.isLink ? (
              <Link key={link.href} to={link.href} className="text-[15px] font-medium text-slate-600 hover:text-[#1A3F6E] transition-colors whitespace-nowrap">{link.label}</Link>
            ) : (
              <a key={link.href} href={link.href} className="text-[15px] font-medium text-slate-600 hover:text-[#1A3F6E] transition-colors whitespace-nowrap">{link.label}</a>
            )
          )}
        </nav>

        <div className="flex items-center gap-2 sm:gap-4">
          <LanguageSwitcher />
          {user ? (
            <Link to="/dashboard"><Button className="bg-[#2CA8AC] hover:bg-[#248F92] rounded-full px-4 sm:px-6 text-sm" data-testid="header-dashboard-btn">Dashboard</Button></Link>
          ) : (
            <>
              <Link to="/login" className="hidden sm:block"><Button variant="ghost" className="text-slate-600 text-[15px]" data-testid="header-login-btn">{t("nav.login")}</Button></Link>
              <Link to="/register" className="hidden sm:block"><Button className="bg-[#2CA8AC] hover:bg-[#248F92] rounded-full px-6 shadow-lg shadow-[#2CA8AC]/20" data-testid="header-register-btn">{t("hero.cta")}</Button></Link>
            </>
          )}
          {/* Mobile hamburger */}
          <Button variant="ghost" size="icon" className="lg:hidden h-9 w-9" onClick={() => setMobileOpen(!mobileOpen)} data-testid="landing-mobile-menu">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile menu dropdown */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-slate-100 shadow-lg animate-in slide-in-from-top-2 duration-200">
          <nav className="max-w-7xl mx-auto px-4 py-4 space-y-1">
            {navLinks.map((link) =>
              link.isLink ? (
                <Link key={link.href} to={link.href} onClick={() => setMobileOpen(false)} className="block px-4 py-3 text-[15px] font-medium text-slate-700 hover:text-[#1A3F6E] hover:bg-slate-50 rounded-lg transition-colors">{link.label}</Link>
              ) : (
                <a key={link.href} href={link.href} onClick={() => setMobileOpen(false)} className="block px-4 py-3 text-[15px] font-medium text-slate-700 hover:text-[#1A3F6E] hover:bg-slate-50 rounded-lg transition-colors">{link.label}</a>
              )
            )}
            {!user && (
              <div className="pt-3 mt-3 border-t border-slate-100 space-y-2 px-4">
                <Link to="/login" onClick={() => setMobileOpen(false)}><Button variant="outline" className="w-full" data-testid="mobile-login-btn">{t("nav.login")}</Button></Link>
                <Link to="/register" onClick={() => setMobileOpen(false)}><Button className="w-full bg-[#2CA8AC] hover:bg-[#248F92]" data-testid="mobile-register-btn">{t("hero.cta")}</Button></Link>
              </div>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}

function HeroSection() {
  const { t } = useI18n();
  const [currentSlide, setCurrentSlide] = useState(0);
  const slides = ["/slide1.png", "/slide2.png", "/slide3.png", "/slide4.png"];

  useEffect(() => {
    const timer = setInterval(() => setCurrentSlide((p) => (p + 1) % slides.length), 4500);
    return () => clearInterval(timer);
  }, [slides.length]);

  return (
    <section className="pt-32 pb-12 sm:pt-40 sm:pb-20 px-6" data-testid="hero-section">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
        <div className="max-w-xl">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-50 to-indigo-50 border border-[#85D8DB]/30 rounded-full px-5 py-2 mb-10 animate-fade-in">
            <Zap className="w-4 h-4 text-[#2CA8AC]" />
            <span className="text-sm font-semibold text-[#1A3F6E]">{t("hero.badge")}</span>
          </div>
          <h1 className="text-[2.8rem] sm:text-[3.2rem] font-bold text-[#1A3F6E] leading-[1.08] animate-fade-in">
            {t("hero.title")}
          </h1>
          <p className="mt-6 text-xl text-[#2CA8AC] font-semibold animate-fade-in stagger-1" style={{opacity:0}}>
            {t("hero.subtitle")}
          </p>
          <p className="mt-6 text-[17px] text-slate-500 leading-relaxed animate-fade-in stagger-2" style={{opacity:0}}>
            {t("hero.description")}
          </p>
          <div className="mt-10 flex flex-wrap gap-4 animate-fade-in stagger-3" style={{opacity:0}}>
            <Link to="/register">
              <Button size="lg" className="bg-[#2CA8AC] hover:bg-[#248F92] text-[16px] px-8 h-14 rounded-full shadow-xl shadow-[#2CA8AC]/25 hover:shadow-[#2CA8AC]/40 transition-all hover:-translate-y-0.5" data-testid="hero-cta-btn">
                {t("hero.cta")} <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href="#pricing">
              <Button size="lg" variant="outline" className="text-[16px] px-8 h-14 rounded-full border-2 border-slate-200 hover:border-[#2CA8AC]/30" data-testid="hero-demo-btn">
                {t("hero.see_pricing")}
              </Button>
            </a>
          </div>
        </div>
        <div className="hidden lg:block animate-fade-in stagger-2" style={{opacity:0}}>
          <div className="relative w-full rounded-2xl overflow-hidden shadow-2xl shadow-slate-900/15 border border-slate-200/50">
            {slides.map((src, i) => (
              <img key={i} src={src} alt={`Smartpriced ${i + 1}`} className={`w-full h-auto block transition-opacity duration-1000 ease-in-out ${i === 0 ? "" : "absolute top-0 left-0"}`} style={{ opacity: i === currentSlide ? 1 : 0 }} />
            ))}
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2.5">
              {slides.map((_, i) => (
                <button key={i} onClick={() => setCurrentSlide(i)} className={`h-3 rounded-full transition-all duration-300 ${i === currentSlide ? "bg-[#2CA8AC] w-8" : "bg-white/50 hover:bg-white/80 w-3"}`} />
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto mt-20 grid grid-cols-2 sm:grid-cols-4 gap-8 border-t border-slate-100 pt-12">
        {[
          { val: "50.000+", label: t("hero.stat_products") },
          { val: "24/7", label: t("hero.stat_monitoring") },
          { val: "99.9%", label: t("hero.stat_uptime") },
          { val: "<5min", label: t("hero.stat_setup") },
        ].map((s, i) => (
          <div key={i} className="text-center sm:text-left">
            <p className="text-3xl font-bold text-[#1A3F6E] font-mono-data">{s.val}</p>
            <p className="text-[15px] text-slate-400 mt-1">{s.label}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function ProblemSection() {
  const { t } = useI18n();
  const pains = [
    { icon: AlertTriangle, title: t("landing.pain1_title"), desc: t("landing.pain1_desc"), color: "from-red-50 to-orange-50", ic: "text-red-500 bg-red-50" },
    { icon: Eye, title: t("landing.pain2_title"), desc: t("landing.pain2_desc"), color: "from-amber-50 to-yellow-50", ic: "text-amber-600 bg-amber-50" },
    { icon: Percent, title: t("landing.pain3_title"), desc: t("landing.pain3_desc"), color: "from-orange-50 to-red-50", ic: "text-orange-500 bg-orange-50" },
  ];
  return (
    <section className="py-24 sm:py-32 px-6 bg-slate-50/50" data-testid="problem-section">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-start gap-6 mb-16">
          <img src="/competitor-illust.png" alt="" className="w-20 h-20 rounded-2xl hidden sm:block" />
          <div>
            <Badge className="bg-red-50 text-red-700 border-red-200 mb-4">{t("landing.problem_badge")}</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A3F6E]">{t("landing.problem_title")}</h2>
            <p className="mt-4 text-lg text-slate-500 max-w-xl">{t("landing.problem_subtitle")}</p>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {pains.map((p, i) => (
            <div key={i} className={`bg-gradient-to-br ${p.color} rounded-2xl p-8 border border-white/80 hover:shadow-lg transition-all duration-300 hover:-translate-y-1`}>
              <div className={`w-12 h-12 rounded-xl ${p.ic} flex items-center justify-center mb-6`}><p.icon className="w-6 h-6" /></div>
              <h3 className="text-xl font-bold text-[#1A3F6E] mb-3">{p.title}</h3>
              <p className="text-[15px] text-slate-600 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturesSection() {
  const { t } = useI18n();
  const features = [
    { icon: Eye, title: t("landing.feat1_title"), desc: t("landing.feat1_desc"), accent: "from-blue-500 to-cyan-500", bg: "bg-[#85D8DB]/100/5", iconBg: "bg-[#85D8DB]/100/10 text-[#2CA8AC]" },
    { icon: Target, title: t("landing.feat2_title"), desc: t("landing.feat2_desc"), accent: "from-emerald-500 to-teal-500", bg: "bg-emerald-500/5", iconBg: "bg-emerald-500/10 text-emerald-600" },
    { icon: TrendingUp, title: t("landing.feat3_title"), desc: t("landing.feat3_desc"), accent: "from-amber-500 to-orange-500", bg: "bg-amber-500/5", iconBg: "bg-amber-500/10 text-amber-600" },
    { icon: Bell, title: t("landing.feat4_title"), desc: t("landing.feat4_desc"), accent: "from-purple-500 to-pink-500", bg: "bg-purple-500/5", iconBg: "bg-purple-500/10 text-purple-600" },
    { icon: LineChart, title: t("landing.feat5_title"), desc: t("landing.feat5_desc"), accent: "from-rose-500 to-red-500", bg: "bg-rose-500/5", iconBg: "bg-rose-500/10 text-rose-600" },
    { icon: DollarSign, title: t("landing.feat6_title"), desc: t("landing.feat6_desc"), accent: "from-teal-500 to-emerald-500", bg: "bg-teal-500/5", iconBg: "bg-teal-500/10 text-teal-600" },
  ];
  return (
    <section id="features" className="py-24 sm:py-32 px-6" data-testid="features-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-20">
          <Badge className="bg-[#85D8DB]/10 text-[#1A3F6E] border-[#2CA8AC]/30 mb-5">{t("landing.features_badge")}</Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1A3F6E]">{t("landing.features_title")}</h2>
          <p className="mt-5 text-lg text-slate-500">{t("landing.features_subtitle")}</p>
        </div>
        {/* Modern bento grid - 2 large + 4 small */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* First feature - large card */}
          <div className="lg:col-span-2 group relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-10 hover:shadow-2xl transition-all duration-500 hover:-translate-y-1">
            <div className={`absolute top-0 right-0 w-64 h-64 bg-gradient-to-br ${features[0].accent} opacity-[0.04] rounded-full -mr-20 -mt-20 group-hover:opacity-[0.08] transition-opacity`}></div>
            <div className={`w-14 h-14 rounded-2xl ${features[0].iconBg} flex items-center justify-center mb-8`}><Eye className="w-7 h-7" /></div>
            <h3 className="text-2xl font-bold text-[#1A3F6E] mb-4">{features[0].title}</h3>
            <p className="text-[16px] text-slate-500 leading-relaxed max-w-lg">{features[0].desc}</p>
            <div className="mt-8 flex gap-3">
              {["Google Shopping", "SERP", "Realtime"].map((tag) => (
                <span key={tag} className="text-xs font-semibold bg-slate-100 text-slate-600 px-3 py-1.5 rounded-full">{tag}</span>
              ))}
            </div>
          </div>
          {/* Second feature */}
          <div className="group relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-8 hover:shadow-2xl transition-all duration-500 hover:-translate-y-1">
            <div className={`absolute top-0 right-0 w-48 h-48 bg-gradient-to-br ${features[1].accent} opacity-[0.04] rounded-full -mr-16 -mt-16 group-hover:opacity-[0.08] transition-opacity`}></div>
            <div className={`w-12 h-12 rounded-2xl ${features[1].iconBg} flex items-center justify-center mb-6`}><Target className="w-6 h-6" /></div>
            <h3 className="text-xl font-bold text-[#1A3F6E] mb-3">{features[1].title}</h3>
            <p className="text-[15px] text-slate-500 leading-relaxed">{features[1].desc}</p>
          </div>
          {/* Third feature */}
          <div className="group relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-8 hover:shadow-2xl transition-all duration-500 hover:-translate-y-1">
            <div className={`absolute top-0 right-0 w-48 h-48 bg-gradient-to-br ${features[2].accent} opacity-[0.04] rounded-full -mr-16 -mt-16 group-hover:opacity-[0.08] transition-opacity`}></div>
            <div className={`w-12 h-12 rounded-2xl ${features[2].iconBg} flex items-center justify-center mb-6`}><TrendingUp className="w-6 h-6" /></div>
            <h3 className="text-xl font-bold text-[#1A3F6E] mb-3">{features[2].title}</h3>
            <p className="text-[15px] text-slate-500 leading-relaxed">{features[2].desc}</p>
            <div className="mt-6 flex gap-2">
              <span className="text-xs font-semibold bg-emerald-50 text-emerald-600 px-3 py-1.5 rounded-full">Accept</span>
              <span className="text-xs font-semibold bg-red-50 text-red-600 px-3 py-1.5 rounded-full">Reject</span>
              <span className="text-xs font-semibold bg-[#85D8DB]/10 text-[#2CA8AC] px-3 py-1.5 rounded-full">Auto</span>
            </div>
          </div>
          {/* Fourth feature - large card */}
          <div className="lg:col-span-2 group relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-10 hover:shadow-2xl transition-all duration-500 hover:-translate-y-1">
            <div className={`absolute top-0 right-0 w-64 h-64 bg-gradient-to-br ${features[3].accent} opacity-[0.04] rounded-full -mr-20 -mt-20 group-hover:opacity-[0.08] transition-opacity`}></div>
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="flex-1">
                <div className={`w-14 h-14 rounded-2xl ${features[3].iconBg} flex items-center justify-center mb-8`}><Bell className="w-7 h-7" /></div>
                <h3 className="text-2xl font-bold text-[#1A3F6E] mb-4">{features[3].title}</h3>
                <p className="text-[16px] text-slate-500 leading-relaxed">{features[3].desc}</p>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                {[{label:t("alert.type.price_change"),color:"text-amber-600 bg-amber-50"},{label:t("alert.type.below_margin"),color:"text-red-600 bg-red-50"},{label:t("alert.type.out_of_stock"),color:"text-[#2CA8AC] bg-[#85D8DB]/10"}].map((a) => (
                  <div key={a.label} className={`${a.color} rounded-xl px-4 py-2.5 text-sm font-medium flex items-center gap-2`}>
                    <div className="w-2 h-2 rounded-full bg-current"></div>{a.label}
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* Fifth + Sixth features - full width row */}
          {features.slice(4).map((f, i) => (
            <div key={i} className={`${i === 0 ? "lg:col-span-2" : "lg:col-span-1"} group relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-8 hover:shadow-2xl transition-all duration-500 hover:-translate-y-1`}>
              <div className={`absolute top-0 right-0 w-48 h-48 bg-gradient-to-br ${f.accent} opacity-[0.04] rounded-full -mr-16 -mt-16 group-hover:opacity-[0.08] transition-opacity`}></div>
              <div className={`w-12 h-12 rounded-2xl ${f.iconBg} flex items-center justify-center mb-6`}><f.icon className="w-6 h-6" /></div>
              <h3 className="text-xl font-bold text-[#1A3F6E] mb-3">{f.title}</h3>
              <p className="text-[15px] text-slate-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ForWhomSection() {
  const { t } = useI18n();
  const audiences = [
    { icon: Package, title: t("landing.audience1_title"), desc: t("landing.audience1_desc"), tags: [t("landing.tag_wholesale"), t("landing.tag_b2b"), t("landing.tag_bulk")] },
    { icon: ShoppingCart, title: t("landing.audience2_title"), desc: t("landing.audience2_desc"), tags: [t("landing.tag_woo"), t("landing.tag_dtp"), t("landing.tag_niche")] },
    { icon: Layers, title: t("landing.audience3_title"), desc: t("landing.audience3_desc"), tags: [t("landing.tag_electronics"), t("landing.tag_fashion"), t("landing.tag_food")] },
  ];
  return (
    <section id="for-whom" className="py-24 sm:py-32 px-6 bg-slate-50/50" data-testid="for-whom-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-20">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1A3F6E]">{t("landing.for_whom_title")}</h2>
          <p className="mt-5 text-lg text-slate-500">{t("landing.for_whom_subtitle")}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {audiences.map((a, i) => (
            <div key={i} className="bg-white rounded-2xl p-10 border border-slate-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="w-14 h-14 rounded-2xl bg-[#2CA8AC] flex items-center justify-center mb-8 shadow-lg shadow-[#2CA8AC]/20">
                <a.icon className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#1A3F6E] mb-4">{a.title}</h3>
              <p className="text-[15px] text-slate-500 leading-relaxed mb-6">{a.desc}</p>
              <div className="flex flex-wrap gap-2">
                {a.tags.map((tag, j) => (<span key={j} className="text-xs font-semibold bg-slate-100 text-slate-600 px-3 py-1.5 rounded-full">{tag}</span>))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  const { t } = useI18n();
  const steps = [
    { num: "01", icon: ShoppingCart, title: t("landing.how_step1_title"), desc: t("landing.how_step1_desc") },
    { num: "02", icon: Activity, title: t("landing.how_step2_title"), desc: t("landing.how_step2_desc") },
    { num: "03", icon: TrendingUp, title: t("landing.how_step3_title"), desc: t("landing.how_step3_desc") },
  ];
  return (
    <section className="py-24 sm:py-32 px-6" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-20">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1A3F6E]">{t("landing.how_title")}</h2>
          <p className="mt-5 text-lg text-slate-500">{t("landing.how_subtitle")}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-12 relative">
          <div className="hidden md:block absolute top-24 left-[20%] right-[20%] h-[2px] bg-gradient-to-r from-blue-200 via-blue-400 to-blue-200"></div>
          {steps.map((s, i) => (
            <div key={i} className="relative text-center group">
              <div className="w-20 h-20 rounded-3xl bg-[#2CA8AC] flex items-center justify-center mx-auto mb-8 shadow-xl shadow-[#2CA8AC]/20 relative z-10 group-hover:scale-110 transition-transform">
                <s.icon className="w-9 h-9 text-white" />
              </div>
              <span className="text-6xl font-bold text-slate-100 absolute top-0 right-8 select-none">{s.num}</span>
              <h3 className="text-xl font-bold text-[#1A3F6E] mb-4">{s.title}</h3>
              <p className="text-[15px] text-slate-500 leading-relaxed max-w-xs mx-auto">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function NumbersSection() {
  const { t } = useI18n();
  return (
    <section className="py-20 px-6 bg-gradient-to-r from-[#1A3F6E] to-[#2CA8AC]" data-testid="numbers-section">
      <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-10">
        {[
          { val: "2.4M+", label: t("landing.num_scans") },
          { val: "847K", label: t("landing.num_saved") },
          { val: "98.7%", label: t("landing.num_accuracy") },
          { val: "150+", label: t("landing.num_shops") },
        ].map((n, i) => (
          <div key={i} className="text-center">
            <p className="text-3xl sm:text-4xl font-bold text-white font-mono-data">{n.val}</p>
            <p className="text-[15px] text-blue-200 mt-3">{n.label}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function PricingSection() {
  const { t } = useI18n();
  const [freq, setFreq] = useState("monthly");
  const [plans, setPlans] = useState(FALLBACK_PLANS);

  // Prices come from the same source as the admin Pricing Matrix; the static list is only a fallback
  useEffect(() => {
    api.get("/subscriptions/plans").then((r) => {
      const live = (r.data || []).filter((p) => p.active !== false && p.prices).sort((a, b) => a.product_limit - b.product_limit)
        .map((p) => ({ name: p.name, limit: p.product_limit, prices: p.prices, popular: p.slug === "small" }));
      if (live.length) setPlans(live);
    }).catch(() => {});
  }, []);
  return (
    <section id="pricing" className="py-24 sm:py-32 px-6" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <Badge className="bg-[#85D8DB]/10 text-[#1A3F6E] border-[#2CA8AC]/30 mb-5">{t("landing.pricing_badge")}</Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1A3F6E]">{t("landing.pricing_title")}</h2>
          <p className="mt-5 text-lg text-slate-500 max-w-lg mx-auto">{t("landing.pricing_subtitle")}</p>
        </div>
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-slate-100 rounded-2xl p-2" data-testid="pricing-frequency-toggle">
            {[["monthly",t("pricing.monthly")],["weekly",t("pricing.weekly")],["daily",t("pricing.daily")]].map(([k,l]) => (
              <button key={k} onClick={() => setFreq(k)} className={`px-7 py-3 rounded-xl text-sm font-semibold transition-all ${freq===k?"bg-white text-[#1A3F6E] shadow-md":"text-slate-500"}`} data-testid={`pricing-freq-${k}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((plan, i) => (
            <div key={i} className={`relative bg-white rounded-2xl border p-8 hover:-translate-y-1 transition-all duration-300 ${plan.popular?"border-blue-600 shadow-xl shadow-[#2CA8AC]/10 ring-2 ring-blue-600":"border-slate-200 hover:shadow-xl"}`} data-testid={`plan-card-${plan.name.toLowerCase().replace(/\s/g,"-")}`}>
              {plan.popular && <div className="absolute -top-4 left-1/2 -translate-x-1/2"><Badge className="bg-[#2CA8AC] shadow-lg px-4 py-1">{t("pricing.popular")}</Badge></div>}
              <h3 className="text-lg font-bold text-[#1A3F6E]">{plan.name}</h3>
              <p className="text-sm text-slate-500 mt-1">{plan.limit.toLocaleString()} {t("pricing.products")}</p>
              <div className="mt-6 mb-7">
                <span className="font-mono-data text-5xl font-bold text-[#1A3F6E]">{plan.prices[freq]}</span>
                <span className="text-slate-400 ml-1">EUR {t("pricing.per_month")}</span>
              </div>
              <ul className="space-y-3 mb-8">
                {[t("landing.plan_feat1"),t("landing.plan_feat2"),t("landing.plan_feat3"),t("landing.plan_feat4")].map((f,j) => (
                  <li key={j} className="flex items-center gap-3 text-[15px] text-slate-600"><Check className="w-5 h-5 text-emerald-500 shrink-0"/>{f}</li>
                ))}
              </ul>
              <Link to="/register">
                <Button className={`w-full h-12 rounded-xl text-[15px] ${plan.popular?"bg-[#2CA8AC] hover:bg-[#248F92] shadow-lg shadow-[#2CA8AC]/20":""}`} variant={plan.popular?"default":"outline"} data-testid={`plan-cta-${plan.name.toLowerCase().replace(/\s/g,"-")}`}>
                  {t("pricing.choose")} <ChevronRight className="w-4 h-4 ml-1"/>
                </Button>
              </Link>
            </div>
          ))}
        </div>
        <p className="text-center text-sm text-slate-400 mt-10">{t("landing.pricing_note")}</p>
      </div>
    </section>
  );
}

function IntegrationSection() {
  const { t } = useI18n();
  return (
    <section className="py-24 sm:py-32 px-6 bg-slate-50/50" data-testid="integrations-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 mb-5">WooCommerce</Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1A3F6E]">{t("landing.woo_title")}</h2>
          <p className="mt-5 text-lg text-slate-500">{t("landing.woo_subtitle")}</p>
        </div>
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: steps */}
          <div className="space-y-6">
            {[
              { num: "1", text: t("landing.woo_step1"), time: "30 sec" },
              { num: "2", text: t("landing.woo_step2"), time: "15 sec" },
              { num: "3", text: t("landing.woo_step3"), time: "10 sec" },
            ].map((step, i) => (
              <div key={i} className="flex items-center gap-5 bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5">
                <div className="w-14 h-14 rounded-2xl bg-emerald-600 flex items-center justify-center text-white text-xl font-bold shrink-0 shadow-lg shadow-emerald-500/20">{step.num}</div>
                <div className="flex-1">
                  <p className="font-semibold text-[#1A3F6E]">{step.text}</p>
                </div>
                <Badge variant="outline" className="text-emerald-600 border-emerald-200 shrink-0">{step.time}</Badge>
              </div>
            ))}
            <div className="flex items-center gap-3 pt-4">
              <div className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse"></div>
              <p className="text-sm font-semibold text-emerald-600">{t("landing.woo_time")}</p>
            </div>
          </div>
          {/* Right: screenshot */}
          <div className="relative">
            <img src="/woo-setup.png" alt="WooCommerce Shop Toevoegen" className="w-full rounded-2xl shadow-2xl shadow-slate-900/10 border border-slate-200/50" />
          </div>
        </div>
      </div>
    </section>
  );
}

function FaqSection() {
  const { t } = useI18n();
  const [tab, setTab] = useState("shop");
  const shopFaqs = [
    { q: t("landing.faq1_q"), a: t("landing.faq1_a") }, { q: t("landing.faq2_q"), a: t("landing.faq2_a") },
    { q: t("landing.faq3_q"), a: t("landing.faq3_a") }, { q: t("landing.faq4_q"), a: t("landing.faq4_a") },
    { q: t("landing.faq5_q"), a: t("landing.faq5_a") }, { q: t("landing.faq6_q"), a: t("landing.faq6_a") },
  ];
  const affFaqs = [
    { q: t("afaq.q1"), a: t("afaq.a1") },
    { q: t("afaq.q2"), a: t("afaq.a2") },
    { q: t("afaq.q3"), a: t("afaq.a3") },
    { q: t("afaq.q4"), a: t("afaq.a4") },
    { q: t("afaq.q5"), a: t("afaq.a5") },
  ];
  const { isEnabled } = useFeatures();
  const showAffiliate = isEnabled("affiliate_module");
  const faqs = tab === "shop" || !showAffiliate ? shopFaqs : affFaqs;
  return (
    <section id="faq" className="py-24 sm:py-32 px-6" data-testid="faq-section">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1A3F6E]">{t("landing.faq_title")}</h2>
        </div>
        <div className="flex justify-center mb-10">
          <div className="inline-flex bg-slate-100 rounded-2xl p-2">
            <button onClick={() => setTab("shop")} className={`px-7 py-3 rounded-xl text-sm font-semibold transition-all ${tab === "shop" ? "bg-white text-[#1A3F6E] shadow-md" : "text-slate-500"}`}>{t("faq.tab_shops")}</button>
            {showAffiliate && <button onClick={() => setTab("affiliate")} className={`px-7 py-3 rounded-xl text-sm font-semibold transition-all ${tab === "affiliate" ? "bg-white text-[#1A3F6E] shadow-md" : "text-slate-500"}`}>{t("faq.tab_affiliate")}</button>}
          </div>
        </div>
        <Accordion type="single" collapsible className="space-y-4">
          {faqs.map((faq, i) => (
            <AccordionItem key={`${tab}-${i}`} value={`faq-${tab}-${i}`} className="bg-white border border-slate-200 rounded-2xl px-7 shadow-sm" data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-left font-semibold text-[15px] text-[#1A3F6E] hover:text-[#2CA8AC] py-6">{faq.q}</AccordionTrigger>
              <AccordionContent className="text-slate-500 pb-6 text-[15px] leading-relaxed">{faq.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function RegisterSection() {
  const { t } = useI18n();
  const { isEnabled } = useFeatures();
  const showAffiliate = isEnabled("affiliate_module");
  return (
    <section className="py-24 sm:py-32 px-6 bg-slate-50/50" data-testid="register-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1A3F6E]">{t("landing.register_title")}</h2>
          <p className="mt-5 text-lg text-slate-500">{t("landing.register_subtitle")}</p>
        </div>
        <div className={`grid gap-10 mx-auto ${showAffiliate ? "md:grid-cols-2 max-w-4xl" : "max-w-xl"}`}>
          <div className="bg-white rounded-3xl border-2 border-blue-600 p-10 shadow-xl shadow-[#2CA8AC]/10 relative overflow-hidden" data-testid="register-webshop-card">
            <div className="absolute top-0 right-0 w-40 h-40 bg-[#85D8DB]/10 rounded-bl-full -mr-8 -mt-8 opacity-50"></div>
            <div className="relative">
              <div className="w-16 h-16 rounded-2xl bg-[#2CA8AC] flex items-center justify-center mb-8 shadow-xl shadow-[#2CA8AC]/20"><ShoppingCart className="w-8 h-8 text-white"/></div>
              <h3 className="text-2xl font-bold text-[#1A3F6E] mb-3">{t("landing.register_shop_title")}</h3>
              <p className="text-[15px] text-slate-500 leading-relaxed mb-8">{t("landing.register_shop_desc")}</p>
              <ul className="space-y-3 mb-10">
                {[t("landing.register_shop_feat1"),t("landing.register_shop_feat2"),t("landing.register_shop_feat3"),t("landing.register_shop_feat4")].map((f,i) => (
                  <li key={i} className="flex items-center gap-3 text-[15px] text-slate-600"><Check className="w-5 h-5 text-emerald-500 shrink-0"/>{f}</li>
                ))}
              </ul>
              <Link to="/register"><Button size="lg" className="w-full bg-[#2CA8AC] hover:bg-[#248F92] h-14 rounded-xl shadow-lg shadow-[#2CA8AC]/20 text-[16px]" data-testid="register-shop-btn">{t("landing.register_shop_cta")} <ArrowRight className="w-5 h-5 ml-2"/></Button></Link>
            </div>
          </div>
          {showAffiliate && (
          <div className="bg-white rounded-3xl border border-slate-200 p-10 hover:shadow-xl transition-all relative overflow-hidden" data-testid="register-affiliate-card">
            <div className="absolute top-0 right-0 w-40 h-40 bg-emerald-50 rounded-bl-full -mr-8 -mt-8 opacity-50"></div>
            <div className="relative">
              <div className="w-16 h-16 rounded-2xl bg-emerald-600 flex items-center justify-center mb-8 shadow-xl shadow-emerald-500/20"><Users className="w-8 h-8 text-white"/></div>
              <h3 className="text-2xl font-bold text-[#1A3F6E] mb-3">{t("landing.register_affiliate_title")}</h3>
              <p className="text-[15px] text-slate-500 leading-relaxed mb-8">{t("landing.register_affiliate_desc")}</p>
              <ul className="space-y-3 mb-10">
                {[t("landing.register_aff_feat1"),t("landing.register_aff_feat2"),t("landing.register_aff_feat3"),t("landing.register_aff_feat4")].map((f,i) => (
                  <li key={i} className="flex items-center gap-3 text-[15px] text-slate-600"><Check className="w-5 h-5 text-emerald-500 shrink-0"/>{f}</li>
                ))}
              </ul>
              <Link to="/register?type=affiliate"><Button size="lg" variant="outline" className="w-full h-14 rounded-xl border-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-[16px]" data-testid="register-affiliate-btn">{t("landing.register_affiliate_cta")} <ArrowRight className="w-5 h-5 ml-2"/></Button></Link>
            </div>
          </div>
          )}
        </div>
      </div>
    </section>
  );
}

function ScreenshotGallery() {
  const { t } = useI18n();
  const [lightbox, setLightbox] = useState(null);
  const screens = [
    { src: "/slide1.png", title: t("gallery.slide1") },
    { src: "/slide2.png", title: t("gallery.slide2") },
    { src: "/slide3.png", title: t("gallery.slide3") },
    { src: "/slide4.png", title: t("gallery.slide4") },
  ];
  return (
    <section className="py-24 sm:py-32 px-6" data-testid="screenshot-gallery">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1A3F6E]">{t("gallery.title")}</h2>
          <p className="mt-4 text-lg text-slate-500">{t("gallery.subtitle")}</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-6">
          {screens.map((s, i) => (
            <button key={i} onClick={() => setLightbox(i)} className="group text-left rounded-2xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 bg-white">
              <div className="overflow-hidden">
                <img src={s.src} alt={s.title} className="w-full h-auto group-hover:scale-[1.02] transition-transform duration-500" style={{imageRendering: "auto"}} loading="lazy" />
              </div>
              <div className="p-4 flex items-center justify-between">
                <span className="font-medium text-[#1A3F6E] text-sm">{s.title}</span>
                <span className="text-xs text-[#2CA8AC] font-medium opacity-0 group-hover:opacity-100 transition-opacity">{t("gallery.enlarge")}</span>
              </div>
            </button>
          ))}
        </div>
      </div>
      {lightbox !== null && (
        <div className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <div className="relative max-w-[95vw] max-h-[95vh] animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <img src={screens[lightbox].src} alt={screens[lightbox].title} className="max-w-full max-h-[85vh] w-auto h-auto rounded-xl shadow-2xl" style={{imageRendering: "auto"}} />
            <p className="text-white text-center mt-4 font-medium">{screens[lightbox].title}</p>
            <button onClick={() => setLightbox(null)} className="absolute -top-3 -right-3 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center text-slate-600 hover:text-slate-900">
              <span className="text-xl leading-none">&times;</span>
            </button>
            <div className="flex justify-center gap-3 mt-4">
              {screens.map((_, i) => (
                <button key={i} onClick={() => setLightbox(i)} className={`w-16 h-10 rounded-lg overflow-hidden border-2 transition-all ${i === lightbox ? "border-[#2CA8AC]" : "border-white/30 hover:border-white/60"}`}>
                  <img src={screens[i].src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function FinalCTA() {
  const { t } = useI18n();
  return (
    <section className="py-24 sm:py-32 px-6 bg-[#1A3F6E]" data-testid="final-cta-section">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="text-3xl sm:text-4xl font-bold text-white">{t("landing.cta_title")}</h2>
        <p className="mt-6 text-xl text-slate-400">{t("landing.cta_subtitle")}</p>
        <div className="mt-12">
          <Link to="/register">
            <Button size="lg" className="bg-[#2CA8AC] hover:bg-[#3BB5B9] text-lg px-12 h-16 rounded-full shadow-2xl shadow-[#2CA8AC]/30 hover:-translate-y-0.5 transition-all" data-testid="final-cta-btn">
              {t("hero.cta")} <ArrowRight className="w-5 h-5 ml-2"/>
            </Button>
          </Link>
        </div>
        <p className="mt-8 text-sm text-slate-500">{t("landing.cta_note")}</p>
      </div>
    </section>
  );
}

function Footer() {
  const { t } = useI18n();
  return (
    <footer className="py-16 px-6 border-t border-slate-200 bg-white">
      <div className="max-w-7xl mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
        <div>
          <img src="/logo.jpg" alt="Smartpriced" className="h-8 mb-4"/>
          <p className="text-sm text-slate-500 leading-relaxed">{t("landing.footer_desc")}</p>
        </div>
        <div>
          <h4 className="font-semibold text-[#1A3F6E] text-sm mb-4">{t("landing.footer_product")}</h4>
          <ul className="space-y-3 text-sm text-slate-500">
            <li><a href="#features" className="hover:text-[#2CA8AC] transition-colors">{t("landing.nav_features")}</a></li>
            <li><a href="#pricing" className="hover:text-[#2CA8AC] transition-colors">{t("nav.pricing")}</a></li>
            <li><a href="#faq" className="hover:text-[#2CA8AC] transition-colors">FAQ</a></li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold text-[#1A3F6E] text-sm mb-4">{t("landing.footer_company")}</h4>
          <ul className="space-y-3 text-sm text-slate-500">
            <li>SB Company</li><li>A. Rodenbachwijk 12, 8792 Waregem</li><li>BTW: BE0503919750</li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold text-[#1A3F6E] text-sm mb-4">Contact</h4>
          <ul className="space-y-3 text-sm text-slate-500"><li>info@smartpriced.io</li><li>+32 9 123 45 67</li></ul>
        </div>
      </div>
      <div className="border-t border-slate-100 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-xs text-slate-400">2024 SB Company. {t("landing.footer_rights")}</p>
        <div className="flex items-center gap-5">
          <Link to="/terms" className="text-xs text-slate-400 hover:text-[#2CA8AC] transition-colors">Algemene Voorwaarden</Link>
          <Link to="/privacy" className="text-xs text-slate-400 hover:text-[#2CA8AC] transition-colors">Privacy Policy</Link>
          <div className="flex items-center gap-1"><Lock className="w-3 h-3 text-slate-400"/><span className="text-xs text-slate-400">{t("landing.footer_secure")}</span></div>
        </div>
      </div>
    </footer>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <Header />
      <HeroSection />
      <ProblemSection />
      <FeaturesSection />
      <ForWhomSection />
      <HowItWorksSection />
      <NumbersSection />
      <PricingSection />
      <IntegrationSection />
      <FaqSection />
      <ScreenshotGallery />
      <RegisterSection />
      <FinalCTA />
      <Footer />
    </div>
  );
}
